/**
 * Thin client for the `kaltura` edge function. Field names mirror our
 * normalized shapes rather than Kaltura's raw DTOs — the edge function
 * owns the translation so the browser never sees `AvatarDto`/`AgentDto`.
 */
import { createLogger } from "@/lib/frontend-logger";
import { CORRELATION_HEADER, nextCorrelationId } from "@/lib/correlation";
import { appendOp } from "@/lib/opSlug";
import { TOKEN_KEY } from "@/lib/auth/PowtoonAuthContext";
import { getOrgDomain } from "@/lib/powtoon-oauth";
import type { KalturaKnowledgeSource } from "@/lib/powtoon-be/types";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
const ENDPOINT = `${SUPABASE_URL}/functions/v1/kaltura`;

const logger = createLogger("kaltura.client");

function powtoonHeaders(): Record<string, string> {
  if (typeof window === "undefined") return {};
  const headers: Record<string, string> = {};
  const token = window.sessionStorage.getItem(TOKEN_KEY);
  const org = getOrgDomain();
  if (token) headers["x-powtoon-token"] = token;
  if (org) headers["x-powtoon-org"] = org;
  return headers;
}

/** Error carrying the upstream HTTP status so callers can decide on retries. */
export class KalturaCallError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "KalturaCallError";
    this.status = status;
  }
}

/** 0 = network failure. Everything else that is worth a second attempt. */
function isTransient(status: number): boolean {
  return status === 0 || status === 408 || status === 429 || status >= 500;
}

/**
 * Retry a transient Kaltura failure a couple of times with jittered backoff.
 * Configuration errors (4xx other than 408/429) fail immediately — retrying
 * them only delays the "unavailable" screen the learner needs to see.
 */
async function withRetry<T>(fn: () => Promise<T>, delaysMs = [400, 1200]): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 0; attempt <= delaysMs.length; attempt++) {
    try {
      return await fn();
    } catch (e) {
      lastErr = e;
      const status = e instanceof KalturaCallError ? e.status : 0;
      if (!isTransient(status) || attempt === delaysMs.length) break;
      const base = delaysMs[attempt];
      const wait = base + Math.round(Math.random() * base * 0.3);
      logger.warn("kaltura call retrying", { attempt: attempt + 1, status, waitMs: wait });
      await new Promise((r) => setTimeout(r, wait));
    }
  }
  throw lastErr;
}

async function call<T>(action: string, params: Record<string, unknown> = {}): Promise<T> {
  const correlationId = nextCorrelationId();
  const startedAt = performance.now();
  let res: Response;
  try {
    res = await fetch(appendOp(ENDPOINT, `kaltura-${action.replace(/_/g, "-")}`), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_KEY,
        authorization: `Bearer ${SUPABASE_KEY}`,
        [CORRELATION_HEADER]: correlationId,
        ...powtoonHeaders(),
      },
      body: JSON.stringify({ action, ...params }),
    });
  } catch (e) {
    const msg = (e as Error)?.message || `kaltura ${action} network error`;
    logger.error("kaltura call network error", { action, correlationId, error: msg });
    throw new KalturaCallError(msg, 0);
  }
  const durationMs = Math.round(performance.now() - startedAt);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg =
      (data && (data.error || data.message)) || `kaltura ${action} failed (${res.status})`;
    logger.error("kaltura call failed", {
      action,
      status: res.status,
      correlationId,
      durationMs,
      error: msg,
    });
    throw new KalturaCallError(msg, res.status);
  }
  logger.info("kaltura call ok", { action, status: res.status, correlationId, durationMs });
  return data as T;
}

/** The scenario-owned visual+voice pair, as returned by create/update. */
export type KalturaAvatar = {
  avatar_id: string;
  name?: string;
  thumbnail_url?: string;
  voice_id?: string;
  visual_id?: string;
};

export type KalturaAgentResult = {
  agent_id: string;
  intellect_id?: number;
  app_guid?: string;
};

/** A catalog building block: a face (`visual`) or a voice. */
export type KalturaCatalogItem = {
  item_id: string;
  type: "visual" | "voice";
  name?: string;
  image_url?: string;
  /** Visual only: "Color" (flat backdrop) or "Image" (scene backdrop). */
  background?: string;
  gender?: string;
  age_group?: string;
  /** Voice only. */
  language?: string;
  description?: string;
};

/**
 * Agent-runtime bootstrap: the conversation KS plus the transport hosts the
 * Agent SDK needs. Kaltura owns the conversation loop in this mode.
 */
export type KalturaAgentSessionInit = {
  widget_id: string;
  token: string;
  /** Required in the SDK's socket query; see the edge function for why. */
  partner_id?: string;
  conversation_manager_url?: string;
  /** Host the SDK POSTs blocking client-tool ACKs to (`respondToTool`). */
  genie_url?: string;

  srs_base_url?: string;
  turn_server_url?: string;
  turn_credentials?: { username: string; credential: string; expiry?: number };
  avatars?: Array<{ id?: string; previewImageUrl?: string; loadingVideoUrl?: string }>;
};


export const kaltura = {
  /** Browse catalog faces or voices for the avatar builder. */
  listCatalog: (input: { type: "visual" | "voice"; language?: string; limit?: number }) =>
    call<{ items: KalturaCatalogItem[]; total: number }>("list_catalog", input),
  /**
   * Compose a face + voice into the scenario's own avatar. Created once on the
   * first save, then updated in place by `updateAvatar`.
   */
  createAvatar: (input: {
    visual_id: string;
    voice_id: string;
    name?: string;
    opening_phrase?: string;
    voice_speed?: number;
  }) => call<{ avatar: KalturaAvatar }>("create_avatar", input),
  /**
   * Re-point an existing avatar at another face/voice. A scenario keeps its
   * avatar id for life, so edits go through here instead of `createAvatar`.
   */
  updateAvatar: (input: {
    avatar_id: string;
    visual_id?: string;
    voice_id?: string;
    name?: string;
    opening_phrase?: string;
    voice_speed?: number;
  }) => call<{ avatar: KalturaAvatar }>("update_avatar", input),
  // Capability flags are REQUIRED on both writes: Kaltura's `capabilities`
  // sub-dict is full-replace, so an omitted flag silently lands as "off" and
  // drifts from the scenario record. Derive them with `kalturaCapabilities`.
  createAgent: (input: {
    name?: string;
    avatar_id: string;
    system_prompt: string;
    opening_text?: string;
    language?: string;
    knowledge_id?: number;
    /** Conversational-only: live web search capability. */
    web_search: boolean;
    /** Conversational-only: learner screen-share vision capability. */
    screen_share: boolean;
    /** Client tools (presentation) the brain may call. Full-replace. */
    tool_ids?: string[];
  }) => call<KalturaAgentResult>("create_agent", input),

  updateAgent: (input: {
    agent_id: string;
    intellect_id?: number;
    name?: string;
    avatar_id?: string;
    system_prompt?: string;
    opening_text?: string;
    language?: string;
    /** Knowledge record to attach; `null` detaches. */
    knowledge_id: number | null;
    web_search: boolean;
    screen_share: boolean;
    /** Client tools (presentation) the brain may call. Full-replace. */
    tool_ids?: string[];
  }) => call<KalturaAgentResult>("update_agent", input),
  deleteAgent: (agent_id: string) => call<{ ok: true }>("delete_agent", { agent_id }),
  /** Knowledge Service (RAG) — one record per scenario. */
  createKnowledge: (input: { name?: string; description?: string }) =>
    call<{ knowledge_id: number; status?: string }>("create_knowledge", input),
  syncKnowledgeSources: (input: {
    knowledge_id: number;
    scenario_id: string;
    sources: Array<
      | { kind: "upload"; source_key: string; object_id: string; file_name: string; mime?: string }
      | { kind: "url"; source_key: string; url: string }
    >;
    existing_sources?: KalturaKnowledgeSource[];
  }) =>
    call<{
      knowledge_id: number;
      status?: string;
      sources: KalturaKnowledgeSource[];
    }>(
      "update_knowledge_sources",
      input,
    ),
  // --- Client tools (`/v1/tool/*`) backing the presentation capability ---
  // Read-only on purpose: the four presentation tools live in the partner-level
  // namespace and are created once, out of band. Saves only attach their ids.
  toolList: () =>
    call<{
      tools: Array<{
        id: string | null;
        name: string | null;
        type: string | null;
        wait_for_response: boolean | null;
      }>;
      totalCount: number;
    }>("tool_list", {}),


  probeState: (input: { agent_id?: string; intellect_id?: number; knowledge_id?: number }) =>
    call<{
      agent?: unknown;
      intellect?: unknown;
      knowledge?: unknown;
    }>("probe_state", input),
  /** Describe one sampled screen-share JPEG for live context injection. */
  describeScreen: (input: { image_base64: string; mime?: string }) =>
    call<{ description: string; analyzed_at: string }>("describe_screen", input),
  /**
   * Bootstrap the Agent runtime for a scenario's agent. Returns a conversation
   * KS scoped to that agent plus the socket/WHEP/TURN hosts.
   */
  agentSessionInit: (input: { agent_id: string }) =>
    withRetry(() => call<KalturaAgentSessionInit>("agent_session_init", input)),
};
